import algosdk from "algosdk";
import {
  SwapType,
  convertFromBaseUnits,
  applySlippageToAmount,
  MINIMUM_BALANCE_REQUIRED_PER_ASSET,
  convertToBaseUnits,
  SwapQuote,
  SwapQuoteType,
  getAssetInAndAssetOutFromSwapQuote,
  getSwapQuotePriceImpact,
  SwapQuoteError,
  SwapQuoteErrorType,
  getV2MinSwapAssetInAmount,
  Swap,
  SignerTransaction,
  combineAndRegroupSignerTxns,
  generateOptIntoAssetTxns,
  ALGO_ASSET,
  getAssetInFromSwapQuote,
  getSwapTotalFee,
  CONTRACT_VERSION,
  SwapRouterResponse
} from "@tinymanorg/tinyman-js-sdk";
import {AssetWithIdAndAmount} from "@tinymanorg/tinyman-js-sdk/dist/util/asset/assetModels";

import {AssetListItemData, SimpleAsset} from "../../asset/util/assetModels";
import {
  canAssetBeUsedAsPoolConstituent,
  formatAssetAmount,
  generateAlgoAssetListItemData,
  generateUSDCAssetListItemData,
  getAssetBalance,
  getAssetId,
  getAssetPairDetail,
  hasAlreadyOptedIntoAsset,
  isALGO,
  isValidAssetID
} from "../../asset/util/assetUtils";
import {AppStateAccountData} from "../../wallet/account/accountTypes";
import {
  convertToStringRepresentation,
  isPositiveNumber,
  parseAsFloat,
  percentToDecimal,
  roundNumber
} from "../../core/util/number/numberUtils";
import {initialSwapFormState} from "./context/SwapFormContext.constants";
import SwapError from "../error/SwapError";
import {
  SwapFormState,
  SwapFormTextFieldNames,
  SwapImpact,
  SwapFundSufficiencyInformation
} from "./SwapForm.types";
import {algod} from "../../core/integrations/algosdk/AlgodManager";
import {PREFERRED_NETWORK, SWAP_SLIPPAGE} from "../../core/constants";
import {generateErrorMessage} from "../../core/util/error/errorUtils";
import {SwapWidgetSearchParamKey} from "../../core/widgetTypes";
import {DEFAULT_SWAP_ASSET_IDS} from "./SwapForm.constants";
import {getPlatformFeePercentage} from "../../core/widgetUtils";

function generateInitialSwapFormState({account}: {account: AppStateAccountData | null}) {
  const state = {...initialSwapFormState};

  state.assetIn.asset = generateAlgoAssetListItemData(account);
  state.assetOut.asset = generateUSDCAssetListItemData(account);

  return state;
}

function generateSwapQuoteAssetData({
  assetIn,
  assetOut
}: {
  assetIn: SwapFormState["assetIn"];
  assetOut: SwapFormState["assetOut"];
}): {
  asset: SimpleAsset;
  amount: null | bigint;
} {
  const swapType = getSwapTypeFromFormState({assetOut});
  const isFixedInput = swapType === SwapType.FixedInput;

  const assetIN_asset = assetIn.asset;
  const assetIN_amount = assetIn[SwapFormTextFieldNames.AssetInAmount];
  const assetOUT_asset = assetOut.asset;
  const assetOUT_amount = assetOut[SwapFormTextFieldNames.AssetOutAmount];

  const quoteAmount = isFixedInput ? assetIN_amount : assetOUT_amount;
  const quoteAsset = isFixedInput ? assetIN_asset : assetOUT_asset;

  if (!quoteAsset) {
    throw new Error(
      "Make sure input and output assets are selected before generating a quote for the swap."
    );
  }

  let parsedQuoteAmount: null | number = parseAsFloat(quoteAmount || 0, 0);

  if (!isPositiveNumber(parsedQuoteAmount)) {
    parsedQuoteAmount = null;
  }

  return {
    asset: quoteAsset,
    amount: parsedQuoteAmount
      ? convertToBaseUnits(quoteAsset.decimals, parsedQuoteAmount)
      : null
  };
}

function calculateEstimatedSwapImpact({
  state: {assetOut, assetIn},
  quote,
  slippage = String(SWAP_SLIPPAGE)
}: {
  state: Pick<SwapFormState, "assetOut" | "assetIn" | "quote">;
  quote: SwapQuote | null;
  slippage?: string;
}): SwapImpact {
  if (!quote) {
    throw new Error("Quote is required to calculate swap impact.");
  }

  const swapType = getSwapTypeFromFormState({assetOut});
  let slippageValue = Number(slippage);

  if (!slippageValue && slippage.startsWith(".")) {
    slippageValue = Number(`0${slippage}`);
  }

  const decimalSlippage = percentToDecimal(slippageValue);

  const {assetIn: quote_AssetIn, assetOut: quote_AssetOut} =
    getAssetInAndAssetOutFromSwapQuote(quote);

  return {
    maximumSent:
      swapType === SwapType.FixedOutput &&
      assetIn.asset &&
      typeof decimalSlippage === "number"
        ? convertFromBaseUnits(
            assetIn.asset.decimals,
            applySlippageToAmount("positive", decimalSlippage, quote_AssetIn.amount)
          )
        : 0,
    minimumReceived:
      swapType === SwapType.FixedInput &&
      assetOut.asset &&
      typeof decimalSlippage === "number"
        ? convertFromBaseUnits(
            assetOut.asset.decimals,
            applySlippageToAmount("negative", decimalSlippage, quote_AssetOut.amount)
          )
        : 0,
    slippage,
    totalFee:
      getSwapFee(quote) +
      getPlatformFeeAmount({
        assetInAmount: quote_AssetIn.amount
      }),
    priceImpact: getSwapQuotePriceImpact(quote)
  };
}

function getSwapFee(quote: SwapQuote) {
  return quote.type === SwapQuoteType.Direct
    ? BigInt(quote.data.quote.swapFee)
    : calculateSwapRouteSwapFee(quote.data);
}

function calculateSwapRouteSwapFee(route: SwapRouterResponse) {
  return BigInt(route.swap_fee ?? 0) * BigInt(route.pool_ids?.length ?? 0);
}

function isFixedInputSwap(state: SwapFormState) {
  return !state.assetOut[SwapFormTextFieldNames.AssetOutAmount];
}

/**
 * Given the pair ratio and price of one of the pairs, determines the other pair's price.
 */
function determinePriceFromPairRatio(
  fractionDecimals: number,
  pairRatio: number,
  otherPairPrice: string | number
): number {
  let price = 0;

  try {
    if (typeof otherPairPrice === "number" || otherPairPrice) {
      const otherPairAmount =
        typeof otherPairPrice === "string" ? parseFloat(otherPairPrice) : otherPairPrice;

      price =
        roundNumber({decimalPlaces: fractionDecimals}, otherPairAmount * pairRatio) || 0;
    }
  } catch (error) {
    price = 0;
  }

  return price;
}

function getSwapTypeFromFormState(
  swapFormState: Pick<SwapFormState, "assetOut">
): SwapType {
  return swapFormState.assetOut[SwapFormTextFieldNames.AssetOutAmount]
    ? SwapType.FixedOutput
    : SwapType.FixedInput;
}

/**
 * It makes a difference for the Tinyman Contracts whether the user has entered an amount for assetIn or assetOut.
 * If the user enters a value to assetIn input, then assetOut input value within the state is empty, "".
 * Therefore, in this case, we get the assetOut input value here from swap quote.
 * Similarly, if the user enters a value to assetOut input, then, assetIn input value within state is empty and we get the input value here from swap quote.
 * */
function determineSwapAmounts(state: SwapFormState) {
  const {quote, assetOut, assetIn} = state;

  const swapAmounts = {
    assetInAmount: assetIn[SwapFormTextFieldNames.AssetInAmount],
    assetOutAmount: assetOut[SwapFormTextFieldNames.AssetOutAmount]
  };

  if (quote) {
    const {
      assetIn: {amount: assetInAmountFromQuote},
      assetOut: {amount: assetOutAmountFromQuote}
    } = getAssetInAndAssetOutFromSwapQuote(quote);

    if (assetOut[SwapFormTextFieldNames.AssetOutAmount] && assetIn.asset) {
      swapAmounts.assetInAmount = convertToStringRepresentation(
        convertFromBaseUnits(assetIn.asset.decimals, assetInAmountFromQuote)
      );
    }

    if (assetIn[SwapFormTextFieldNames.AssetInAmount] && assetOut.asset) {
      swapAmounts.assetOutAmount = convertToStringRepresentation(
        convertFromBaseUnits(assetOut.asset.decimals, assetOutAmountFromQuote)
      );
    }
  }

  return swapAmounts;
}

function hasEnoughFundsToCarryOutSwap({
  account,
  swapFormState,
  accountAssets
}: {
  account: null | AppStateAccountData;
  swapFormState: SwapFormState;
  accountAssets: AssetListItemData[];
}) {
  const balance = account?.amount || 0n;
  const {assetIn} = swapFormState;

  const requiredAlgoBalanceForSwap =
    assetIn.asset && isALGO(getAssetId(assetIn.asset))
      ? getMinRequiredBalanceToSwap({account, accountAssets, swapFormState})
      : getMinRequiredAlgoForSwap({
          account,
          accountAssets,
          swapFormState
        });

  return typeof balance === "bigint" && account
    ? balance >= requiredAlgoBalanceForSwap
    : false;
}

function getMinRequiredAlgoForSwap({
  account,
  swapFormState,
  accountAssets
}: {
  account: null | AppStateAccountData;
  accountAssets: AssetListItemData[];
  swapFormState: SwapFormState;
}) {
  const assetOut = swapFormState.assetOut.asset;
  let minRequiredBalance = account?.minBalance || 0n;

  if (swapFormState.quote) {
    minRequiredBalance += getSwapTotalFee({
      minFee: algod.suggestedTransactionFee,
      version: CONTRACT_VERSION.V2,
      type: getSwapTypeFromFormState(swapFormState)
    });

    const willAssetOptInIncludedInTxnGroup =
      assetOut?.id && !hasAlreadyOptedIntoAsset(accountAssets, Number(assetOut.id));

    if (willAssetOptInIncludedInTxnGroup) {
      const assetOptInRelatedMinBalance =
        // After the transaction, the user will have one more asset, so their minimum balance will increase
        MINIMUM_BALANCE_REQUIRED_PER_ASSET +
        // Txn fee for the opt-in transaction
        algod.suggestedTransactionFee;

      minRequiredBalance += assetOptInRelatedMinBalance;
    }
  }

  return minRequiredBalance;
}

function getMinRequiredBalanceToSwap({
  account,
  accountAssets,
  swapFormState
}: {
  account: null | AppStateAccountData;
  accountAssets: AssetListItemData[];
  swapFormState: SwapFormState;
}) {
  const assetInAmount = Number(determineSwapAmounts(swapFormState).assetInAmount);

  if (!assetInAmount || !swapFormState.assetIn.asset || !swapFormState.quote) {
    return 0n;
  }

  const platformFeeAmount = getPlatformFeeAmount({
    assetInAmount: getAssetInFromSwapQuote(swapFormState.quote).amount
  });

  let balance = convertToBaseUnits(
    swapFormState.assetIn.asset.decimals,
    Number(assetInAmount)
  );

  if (isALGO(getAssetId(swapFormState.assetIn.asset))) {
    balance += getMinRequiredAlgoForSwap({account, accountAssets, swapFormState});
  }

  balance += platformFeeAmount;

  return balance;
}

function shouldPromptInsufficientAlgoBalanceForSwap({
  account,
  swapFormState,
  accountAssets
}: {
  account: null | AppStateAccountData;
  accountAssets: AssetListItemData[];
  swapFormState: SwapFormState;
}) {
  return !hasEnoughFundsToCarryOutSwap({
    account,
    accountAssets,
    swapFormState
  });
}

function getSwapFundSufficiencyInformation({
  account,
  accountAssets,
  swapFormState
}: {
  account: AppStateAccountData | null;
  accountAssets: AssetListItemData[];
  swapFormState: SwapFormState;
}): SwapFundSufficiencyInformation {
  const {assetIn} = swapFormState;
  const assetInBalance = assetIn.asset
    ? getAssetBalance(account, getAssetId(assetIn.asset))
    : 0;
  const hasInsufficientAlgoBalanceForSwap = shouldPromptInsufficientAlgoBalanceForSwap({
    account,
    accountAssets,
    swapFormState
  });
  const isAssetInAlgo = assetIn.asset && isALGO(getAssetId(assetIn.asset));
  const minimumAlgoBalanceToSwap = convertFromBaseUnits(
    ALGO_ASSET.decimals,
    isAssetInAlgo
      ? getMinRequiredBalanceToSwap({account, accountAssets, swapFormState})
      : getMinRequiredAlgoForSwap({account, accountAssets, swapFormState})
  );
  const minRequiredAssetInBalanceToSwap = assetIn.asset
    ? convertFromBaseUnits(
        assetIn.asset.decimals,
        getMinRequiredBalanceToSwap({account, accountAssets, swapFormState})
      )
    : 0;

  const hasEnoughAssetInBalanceForSwap =
    assetIn.asset && assetInBalance
      ? convertFromBaseUnits(assetIn.asset.decimals, assetInBalance) >=
        minRequiredAssetInBalanceToSwap
      : false;

  let insufficientAlgoMessage = "";
  let insufficientInputAssetBalanceMessage = "";

  if (hasInsufficientAlgoBalanceForSwap) {
    insufficientAlgoMessage =
      minimumAlgoBalanceToSwap > 0
        ? `You don't have enough balance. You need to have at least ${minimumAlgoBalanceToSwap} ALGO to carry out this swap.`
        : "You don’t have enough ALGO to carry out this swap.";
  }

  if (!hasEnoughAssetInBalanceForSwap && !isAssetInAlgo) {
    insufficientInputAssetBalanceMessage =
      minRequiredAssetInBalanceToSwap > 0
        ? `You don't have enough balance. You need to have at least ${minRequiredAssetInBalanceToSwap} ${assetIn?.asset?.unit_name} to carry out this swap.`
        : `You don’t have enough ${assetIn?.asset?.unit_name} balance.`;
  }

  return {
    algoBalance: {
      hasEnough: !hasInsufficientAlgoBalanceForSwap,
      message: insufficientAlgoMessage
    },
    assetInBalance: {
      hasEnough: hasEnoughAssetInBalanceForSwap,
      message: insufficientInputAssetBalanceMessage
    }
  };
}

/**
 * Checks the assetIds and return FALSE if
 *  - input and output assets are the same
 *  - one of the input or output assets are missing
 *  - both assets are missing
 *  - if provided inputs are not valid as asset ids
 */
function validateProvidedAssetIdsForSwap(
  assetIN: number | string | null | undefined,
  assetOUT: number | string | null | undefined
) {
  return isValidAssetID(assetIN) && isValidAssetID(assetOUT) && assetIN !== assetOUT;
}

/**
 * Generates the asset IDs for the swap form based on search params.
 * If the asset_in or asset_out is not provided, it will default to ALGO and USDC respectively.
 * If asset_in is not provided and asset_out is ALGO, asset_in will default to USDC.
 * If asset_out is not provided and asset_in is USDC, asset_out will default to ALGO.
 */
function generateSwapFormAssetIDs(): {
  assetInID: number | null;
  assetOutID: number | null;
} {
  const searchParams = new URLSearchParams(document.location.search);

  const [assetInIDFromSearchParams, assetOutIDFromSearchParams] = [
    searchParams.get(SwapWidgetSearchParamKey.ASSET_IN),
    searchParams.get(SwapWidgetSearchParamKey.ASSET_OUT)
  ];

  if (assetInIDFromSearchParams || assetOutIDFromSearchParams) {
    return {
      assetInID: assetInIDFromSearchParams ? Number(assetInIDFromSearchParams) : null,
      assetOutID: assetOutIDFromSearchParams ? Number(assetOutIDFromSearchParams) : null
    };
  }

  return {
    assetInID: Number(DEFAULT_SWAP_ASSET_IDS.IN),
    assetOutID: Number(DEFAULT_SWAP_ASSET_IDS.OUT)
  };
}

async function generateSwapFormAssetData({
  assetIN,
  assetOUT
}: {
  assetIN: number | null;
  assetOUT: number | null;
}): Promise<{
  assetIn: SwapFormState["assetIn"]["asset"];
  assetOut: SwapFormState["assetOut"]["asset"];
}> {
  if (!validateProvidedAssetIdsForSwap(assetIN, assetOUT)) {
    throw new SwapError("InvalidAssetIDs", "Invalid assets ids provided for swap");
  }

  const {asset1Detail: assetInDetail, asset2Detail: assetOutDetail} =
    await getAssetPairDetail(assetIN, assetOUT);

  if (!assetInDetail) {
    throw new SwapError(
      "AssetCannotBeUsedInSwap",
      "Invalid input asset provided to swap."
    );
  } else if (!canAssetBeUsedAsPoolConstituent(assetInDetail)) {
    throw new SwapError(
      "AssetCannotBeUsedInSwap",
      "Input asset cannot be used to swap since it is not a pool member."
    );
  }

  if (!assetOutDetail) {
    throw new SwapError(
      "AssetCannotBeUsedInSwap",
      "Invalid output asset provided to swap."
    );
  } else if (!canAssetBeUsedAsPoolConstituent(assetOutDetail)) {
    throw new SwapError(
      "AssetCannotBeUsedInSwap",
      "Output asset cannot be used to swap since it is not a pool member."
    );
  }

  return {
    assetIn: assetInDetail,
    assetOut: assetOutDetail
  };
}

function generateSwapQuoteErrorMessage({
  error,
  assetIn
}: {
  error: unknown;
  assetIn: SimpleAsset | null;
}): string {
  if (!(error instanceof SwapQuoteError)) {
    // sendSentryAnException(error, {
    //   title: "Failed to fetch swap quote",
    //   data: additionalInformation
    // });

    return "An error occurred while fetching swap quote. Please try again.";
  }

  let errorMessage = error.message;

  switch (error.type) {
    case SwapQuoteErrorType.NoAvailablePoolError:
      errorMessage =
        "There are no available pools or a possible swap route to perform this swap. Please select different assets.";

      break;

    case SwapQuoteErrorType.OutputAmountExceedsAvailableLiquidityError:
      errorMessage =
        "There is not enough liquidity in the pool and there is no possible swap route for this swap. Please try with different amounts.";

      break;

    case SwapQuoteErrorType.SwapRouterInsufficientReservesError:
      errorMessage =
        "The amount you are trying to swap is too large. Please try with lower amounts";
      break;

    case SwapQuoteErrorType.SwapRouterLowSwapAmountError:
      errorMessage =
        "The amount you are trying to swap is too small. Please try with larger amounts.";
      break;

    case SwapQuoteErrorType.LowSwapAmountError: {
      if (assetIn) {
        errorMessage = `The swap amount is too low. The minimum swap amount is ${formatAssetAmount(
          {
            amount: convertFromBaseUnits(assetIn.decimals, getV2MinSwapAssetInAmount()),
            assetDecimals: assetIn.decimals,
            unitName: assetIn.unit_name
          }
        )}.`;
      } else {
        errorMessage = "The swap amount is too low.";
      }
      break;
    }

    default:
      errorMessage =
        "There are no available pools or a possible swap route to perform this swap. Please select different assets.";

      break;
  }

  return errorMessage;
}

async function generateSwapTxns({
  accountAddress,
  accountAssets,
  quote,
  swapType,
  slippage,
  assetOutID
}: {
  accountAddress: string;
  accountAssets: AssetListItemData[];
  quote: SwapQuote;
  swapType: SwapType;
  slippage: number;
  assetOutID: number;
}) {
  let txnsData: SignerTransaction[] = [];
  const isOptedIntoOutputAsset = hasAlreadyOptedIntoAsset(accountAssets, assetOutID);

  const searchParams = new URLSearchParams(document.location.search);
  const platformFeePercentage = getPlatformFeePercentage();
  const platformFeeAccount = searchParams.get(
    SwapWidgetSearchParamKey.PLATFORM_FEE_ACCOUNT
  );

  txnsData = await Swap.v2.generateTxns({
    client: algod.client,
    swapType,
    slippage,
    initiatorAddr: accountAddress,
    quote,
    network: PREFERRED_NETWORK
  });

  //  Add Opt-In Txns if required
  if (assetOutID && !isOptedIntoOutputAsset) {
    txnsData = combineAndRegroupSignerTxns(
      await generateOptIntoAssetTxns({
        client: algod.client,
        assetID: assetOutID,
        initiatorAddr: accountAddress
      }),
      txnsData
    );
  }

  if (platformFeeAccount && platformFeePercentage) {
    txnsData = combineAndRegroupSignerTxns(
      await generatePlatformFeeTransaction({
        accountAddress,
        assetIn: getAssetInFromSwapQuote(quote),
        platformFeeAccount,
        platformFeePercentage
      }),
      txnsData
    );
  }

  return txnsData;
}

function getPlatformFeeAmount({assetInAmount}: {assetInAmount: number | bigint}) {
  const platformFeePercentage = getPlatformFeePercentage();

  return platformFeePercentage
    ? BigInt(Math.floor(Number(assetInAmount) * percentToDecimal(platformFeePercentage)))
    : 0n;
}

async function generatePlatformFeeTransaction({
  accountAddress,
  assetIn,
  platformFeeAccount
}: {
  accountAddress: string;
  assetIn: AssetWithIdAndAmount;
  platformFeePercentage: number;
  platformFeeAccount: string;
}): Promise<SignerTransaction[]> {
  const suggestedParams = await algod.client.getTransactionParams().do();
  const amount = getPlatformFeeAmount({
    assetInAmount: assetIn.amount
  });

  const platformFeeTxn = isALGO(assetIn.id)
    ? algosdk.makePaymentTxnWithSuggestedParamsFromObject({
        sender: accountAddress,
        receiver: platformFeeAccount,
        amount,
        suggestedParams
      })
    : algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
        sender: accountAddress,
        receiver: platformFeeAccount,
        assetIndex: assetIn.id,
        amount,
        suggestedParams
      });

  return [{txn: platformFeeTxn, signers: [accountAddress]}];
}

function getSwapFormErrorMessage(
  fundSufficiencyInfo: SwapFundSufficiencyInformation,
  error?: unknown
) {
  let message = "";

  if (!fundSufficiencyInfo.algoBalance.hasEnough) {
    message = fundSufficiencyInfo.algoBalance.message;
  } else if (!fundSufficiencyInfo.assetInBalance.hasEnough) {
    message = fundSufficiencyInfo.assetInBalance.message;
  } else if (error) {
    message = generateErrorMessage(error);
  }

  return message;
}

export {
  calculateEstimatedSwapImpact,
  determinePriceFromPairRatio,
  generateSwapQuoteAssetData,
  isFixedInputSwap,
  getSwapTypeFromFormState,
  determineSwapAmounts,
  hasEnoughFundsToCarryOutSwap,
  getSwapFee,
  shouldPromptInsufficientAlgoBalanceForSwap,
  getMinRequiredAlgoForSwap,
  getMinRequiredBalanceToSwap,
  getSwapFundSufficiencyInformation,
  generateInitialSwapFormState,
  generateSwapFormAssetIDs,
  generateSwapFormAssetData,
  validateProvidedAssetIdsForSwap,
  generateSwapQuoteErrorMessage,
  generateSwapTxns,
  getSwapFormErrorMessage,
  getPlatformFeeAmount
};

/* eslint
      max-lines: "off"
*/
